import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";
import { useForm, useFieldArray, SubmitHandler, Path, Controller } from "react-hook-form";

import { useEntitySettings } from "../hooks/useEntitySettings";
import { useSettings } from "../hooks/useSettings";
import { useEditEntitySettings } from "../hooks/useEditEntitySettings";
import { SettingEditFormValue } from "../models/SettingEditFormValue";
import { SettingEditForm } from "../models/SettingEditForm";
import { EntityTypeLink } from "../models/enums/EntityTypeLink";
import { EntityType } from "../../settingDefinition/models/enums/EntityType";
import { SettingValueType } from "../../settingDefinition/models/enums/SettingValueType";

import FormCardWrapper from "../../../components/reusable-components/form/FormCardWrapper";
import FormTextField from "../../../components/reusable-components/form/MuiFormInput";
import { useFieldRules } from "../../../hooks/useFieldRules";
import ConfirmDeleteModal from "../../../components/reusable-components/modals/ConfirmDeleteModal";
import { useModal } from "../../../hooks/useModal";
import RichTextForm from "../../../components/reusable-components/form/RichTextForm";

interface EntitySettingsEditFormProps {
  entityTypeLink: EntityTypeLink;
  entityType: EntityType;
  entityId: string;
}

interface FormValues {
  settings: { key: string; value: string | number; id: string }[];
}

const EntitySettingsEditForm = ({
  entityTypeLink,
  entityType,
  entityId,
}: EntitySettingsEditFormProps) => {
  const rules = useFieldRules();

  const {
    data: definitions,
    fetch: {
      error: defError,
      loading: defLoading,
      refetch: refetchDefinitions,
    },
  } = useEntitySettings(entityTypeLink);

  const {
    data: settings,
    fetch: { error: getError, loading: getLoading, refetch: refetchSettings },
  } = useSettings(entityTypeLink, entityId);

  const {
    fetchData: updateSettings,
    success,
    error,
    loading,
  } = useEditEntitySettings(entityTypeLink, entityId);

  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    clearErrors,
    trigger,
  } = useForm<FormValues>({
    defaultValues: { settings: [] },
    mode: "all",
    shouldUnregister: true,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "settings",
    keyName: "internalId",
  });

  const confirmDelete = useModal<{
    key: string;
    index: number;
    label: string;
    value?: string | number;
  }>();

  useEffect(() => {
    if (settings.length > 0) {
      replace(
        settings.map((item) => ({
          ...item,
          id: item.key,
        }))
      );
    }
  }, [settings]);

  const availableDefinitions = definitions.filter(
    (def) => !fields.some((field) => field.key === def.key)
  );

  const handleAddSetting = (key: string) => {
    const definition = definitions.find((d) => d.key === key);
    if (!definition) return;

    append({
      key: definition.key,
      value: String(definition.default_value ?? ""),
      id: definition.key,
    });
  };

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    await updateSettings({
      body: {
        settings: data.settings.map(
          (setting) =>
            new SettingEditForm({
              ...setting,
              entity_type: entityType,
              entity_id: entityId,
            })
        ),
      },
    });

    refetchDefinitions();
    refetchSettings();
  };

  const disabled = isSubmitting || loading || defLoading || getLoading;

  return (
    <>
      <FormCardWrapper
        title={`Edit ${entityTypeLink} Settings`}
        successText="Settings updated successfully!"
        success={success}
        error={error || getError || defError}
        onSubmit={handleSubmit(onSubmit)}
      >
        {defLoading || getLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            {fields.map((field, index) => {
              const def = definitions.find((d) => d.key === field.key);
              const isUnknown = !def;

              return (
                <Paper
                  key={field.internalId}
                  variant="outlined"
                  sx={{
                    p: 2,
                    backgroundColor: (theme) =>
                      isUnknown
                        ? theme.palette.error.light + "22"
                        : theme.palette.background.paper,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Box flex={1}>
                      {def?.type === SettingValueType.Text ||
                      def?.type === SettingValueType.String ? (
                        <RichTextForm
                          name={`settings.${index}.value` as Path<FormValues>}
                          control={control}
                          label={def?.description || field.key}
                          disabled={disabled}
                          rules={
                            def
                              ? rules(def.description || def.key)
                                  .fromType(def.type)
                                  .get()
                              : undefined
                          }
                        />
                      ) : def?.type === SettingValueType.Boolean ? (
                        <Controller
                          name={`settings.${index}.value` as Path<FormValues>}
                          control={control}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Switch
                                  {...field}
                                  checked={String(field.value) === "1"}
                                  onChange={(e) =>
                                    field.onChange(e.target.checked ? "1" : "0")
                                  }
                                  disabled={disabled}
                                />
                              }
                              label={def?.description || field.name}
                            />
                          )}
                        />
                      ) : (
                        <FormTextField<FormValues>
                          name={`settings.${index}.value` as Path<FormValues>}
                          control={control}
                          label={def?.description || field.key}
                          rules={
                            def
                              ? rules(def.description || def.key)
                                  .fromType(def.type)
                                  .get()
                              : undefined
                          }
                          textFieldProps={{
                            type:
                              def?.type === SettingValueType.Integer
                                ? "number"
                                : "text",
                            disabled,
                          }}
                        />
                      )}
                    </Box>

                    <input
                      type="hidden"
                      {...register(`settings.${index}.key`)}
                      value={field.key}
                    />

                    <IconButton
                      onClick={() =>
                        confirmDelete.setTarget({
                          key: field.key,
                          index,
                          label: def?.description || def?.key || field.key,
                          value: field.value,
                        })
                      }
                      disabled={disabled}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              );
            })}

            {availableDefinitions.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel id="add-setting-label">Add Setting</InputLabel>
                <Select
                  labelId="add-setting-label"
                  value=""
                  onChange={(e) => handleAddSetting(e.target.value)}
                  disabled={disabled}
                  label="Add Setting"
                >
                  {availableDefinitions.map((def) => (
                    <MenuItem key={def.key} value={def.key}>
                      {def.description || def.key}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No more settings to add.
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                type="submit"
                loading={isSubmitting || loading}
                disabled={disabled}
              >
                Save Settings
              </Button>
            </Box>
          </Stack>
        )}
      </FormCardWrapper>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        row={confirmDelete.target}
        loading={false}
        onClose={confirmDelete.handleClose}
        onConfirm={() => {
          if (confirmDelete.target) {
            remove(confirmDelete.target.index);
            clearErrors("settings");
            trigger("settings");
            confirmDelete.handleClose();
          }
        }}
        getTitle={(row) => `"${row.key} - ${row.label} - ${row.value}"`}
      />
    </>
  );
};

export default EntitySettingsEditForm;
